import { Injectable } from '@angular/core';
import {
  LoginResponse,
  PersonaConfig,
  RefreshAccessTokenConfig,
} from '@talisdev/persona-padlock';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ErrorWithCause } from '®app/lti/lti-authentication/lti-authentication.component';
import { AppConfig } from '®shared/config';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';

import { AuthenticationFailedDoNotRetryException } from './authentication-failed-do-not-retry-exception';
import { AuthenticationFailedException } from './authentication-failed-exception';
import { PersonaPadlockService } from './persona-padlock.service';
import {
  PersonaUser,
  createPersonaUserFromPkceLogin,
  isPersonaUser,
} from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

const USER_SSO_TIMEOUT_SECONDS = 5 * 60 * 1000;

@Injectable()
export class PersonaPkceSharedService {
  constructor(
    private appConfig: AppConfig,
    private errorReportingService: ErrorReportingService,
    private personaPadlockService: PersonaPadlockService,
    private userScopesService: UserScopesService,
  ) {}

  public throwAuthenticationFailedException(
    error: ErrorWithCause,
  ): Observable<never> {
    this.errorReportingService.reportToInstana(error, {
      cause: error.cause?.message,
    });
    return throwError(
      new AuthenticationFailedException(
        (error && error.message) || 'Unknown authentication error',
      ),
    );
  }

  public getPersonaConfig(authProvider: string): PersonaConfig {
    return {
      clientId: this.appConfig.oauthAppId,
      personaOrigin: this.appConfig.personaOrigin,
      provider: authProvider,
      timeoutMilliseconds: USER_SSO_TIMEOUT_SECONDS,
    };
  }

  public getAuthProvider(isTalisLogin: boolean): string {
    return isTalisLogin
      ? this.appConfig.talisLoginProvider
      : this.appConfig.tenant.shortCode;
  }

  public silentlyGetCurrentToken(
    silentAuthentication$: Observable<LoginResponse>,
  ): Observable<User> {
    return silentAuthentication$.pipe(
      map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
      map(personaUser => {
        if (!this.userScopesService.isAuthorizedAsMemberAtTenant(personaUser)) {
          throw new AuthenticationFailedException(
            'Persona User from another tenancy',
          );
        }
        return personaUser;
      }),
      catchError(error => {
        if (error instanceof AuthenticationFailedDoNotRetryException) {
          this.errorReportingService.reportToInstana(error, {
            cause: error.cause?.message,
          });
          return throwError(error);
        }
        return this.throwAuthenticationFailedException(error);
      }),
    );
  }

  public refreshToken(currentUser: User): Observable<User> {
    if (!isPersonaUser(currentUser)) {
      return throwError(
        new AuthenticationFailedException('Not a persona user'),
      );
    }

    const refreshTokenConfig: RefreshAccessTokenConfig = {
      refreshToken: currentUser.refreshToken,
      personaOrigin: this.appConfig.personaOrigin,
      userGuid: currentUser.guid,
    };

    return this.personaPadlockService
      .refreshAccessToken(refreshTokenConfig)
      .pipe(
        map(
          loginResponse =>
            new PersonaUser({
              guid: currentUser.guid,
              oauth: {
                access_token: loginResponse.access_token,
                refresh_token: loginResponse.refresh_token,
                scope: currentUser.scopes,
                token_type: 'bearer',
              },
              profile: currentUser.profile,
              active_pid: currentUser.activePid,
            }),
        ),
        catchError(error => this.throwAuthenticationFailedException(error)),
      );
  }
}
